import {
  Box,
  Flex,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Text,
} from "@chakra-ui/react"
import { getIterationsPage } from "api/mapping/iteration"
import { FilterChip } from "component/FilterChip"
import { IterationsTable } from "component/mapping/iteration/IterationsTable"
import { LoadingPage } from "component/page/LoadingPage"
import { Page } from "component/page/Page"
import { PageHeading } from "component/page/PageHeading"
import { StickyBottomBar } from "component/page/StickyBottomBar"
import { IterationStatus } from "constant/iterationStatus"
import { IterationType } from "constant/iterationType"
import { useEffect, useState } from "react"
import { FiChevronLeft, FiChevronRight, FiSearch, FiX } from "react-icons/fi"
import { useQuery } from "react-query"
import { IterationsPage } from "type/mapping/iteration"

const PAGE_SIZE = 20
// Запрос — когда перестали печатать, а не на каждую букву
const SEARCH_DELAY_MS = 300

const ALL = ""

const TYPE_FILTERS = [
  { value: ALL, label: "Все" },
  { value: IterationType.UTD, label: "УПД" },
  { value: IterationType.SMR, label: "Сметы" },
]

const STATUS_FILTERS = [
  { value: ALL, label: "Все" },
  { value: IterationStatus.NEED_APPROVE, label: "Нужно проверить" },
  { value: IterationStatus.APPROVED, label: "Проверено" },
]

const sumCounts = (counts?: Record<string, number>) =>
  Object.values(counts || {}).reduce((sum, count) => sum + count, 0)

// История сопоставлений: поиск, фильтры и пагинация — на бэке, по всем проверкам
export const MappingIterations = () => {
  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [type, setType] = useState(ALL)
  const [status, setStatus] = useState(ALL)
  const [page, setPage] = useState(0)

  useEffect(() => {
    const timer = window.setTimeout(
      () => setDebouncedSearch(search.trim()),
      SEARCH_DELAY_MS,
    )
    return () => window.clearTimeout(timer)
  }, [search])

  // Новый поиск или фильтр — с первой страницы
  useEffect(() => setPage(0), [debouncedSearch, type, status])

  const { data, isLoading, isFetching } = useQuery<IterationsPage>(
    ["iterationsPage", debouncedSearch, type, status, page],
    () =>
      getIterationsPage({
        search: debouncedSearch || undefined,
        type: type || undefined,
        status: status || undefined,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      }),
    // Пока грузится следующая страница, видна текущая, а не пустой экран
    { keepPreviousData: true },
  )

  const total = data?.total ?? 0
  const pagesCount = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const from = total ? page * PAGE_SIZE + 1 : 0
  const to = Math.min(total, (page + 1) * PAGE_SIZE)

  const countType = (value: string) =>
    value === ALL ? sumCounts(data?.type_counts) : (data?.type_counts[value] ?? 0)
  const countStatus = (value: string) =>
    value === ALL ? sumCounts(data?.status_counts) : (data?.status_counts[value] ?? 0)

  return (
    <Page>
      <PageHeading>История сопоставлений</PageHeading>

      {/* Поиск и фильтры */}
      <Flex w="full" wrap="wrap" align="center" gap={2}>
        <InputGroup w="420px" maxW="full" mr={2}>
          <InputLeftElement pointerEvents="none">
            <Icon as={FiSearch} color="gray.400" />
          </InputLeftElement>
          <Input
            placeholder="Номер УПД, смета, объект, ИНН или ID"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <InputRightElement>
              <IconButton
                aria-label="Очистить поиск"
                icon={<Icon as={FiX} />}
                size="sm"
                variant="ghost"
                color="gray.500"
                onClick={() => setSearch("")}
              />
            </InputRightElement>
          )}
        </InputGroup>

        {TYPE_FILTERS.map((option) => (
          <FilterChip
            key={option.value}
            isActive={type === option.value}
            onClick={() => setType(option.value)}
          >
            {`${option.label} · ${countType(option.value)}`}
          </FilterChip>
        ))}

        <Box w="1px" h={6} bg="gray.200" mx={1} />

        {STATUS_FILTERS.map((option) => (
          <FilterChip
            key={option.value}
            isActive={status === option.value}
            onClick={() => setStatus(option.value)}
          >
            {`${option.label} · ${countStatus(option.value)}`}
          </FilterChip>
        ))}
      </Flex>

      {isLoading && <LoadingPage />}

      {/* Пока идёт запрос, старые строки бледнее — видно, что список обновляется */}
      {!isLoading && data && (
        <Box opacity={isFetching ? 0.6 : 1} transition="opacity 0.15s ease">
          <IterationsTable iterations={data.items} />
        </Box>
      )}

      {/* Пагинация: прилипает к низу экрана, листать до конца не нужно */}
      {!isLoading && total > 0 && (
        <StickyBottomBar justifyContent="flex-end" fontSize="sm" color="gray.600">
          {/* Какие строки списка на экране: с 1-й по 20-ю из всех найденных */}
          <Text mr={2}>
            Показаны {from}–{to} из {total}
          </Text>

          <Flex align="center" gap={2}>
            <IconButton
              aria-label="Предыдущая страница"
              icon={<Icon as={FiChevronLeft} />}
              size="sm"
              variant="outline"
              isDisabled={page === 0}
              onClick={() => setPage((prev) => prev - 1)}
            />
            {/* Жирность как у заголовков колонок (Th — bold, в теме 600) */}
            <Text minW="88px" textAlign="center" fontWeight="bold">
              {page + 1} из {pagesCount}
            </Text>
            <IconButton
              aria-label="Следующая страница"
              icon={<Icon as={FiChevronRight} />}
              size="sm"
              variant="outline"
              isDisabled={page + 1 >= pagesCount}
              onClick={() => setPage((prev) => prev + 1)}
            />
          </Flex>
        </StickyBottomBar>
      )}
    </Page>
  )
}
